import TYPES from '@/types';
import i18n from '@/vue-app/plugins/i18n';
import { currencyFormat } from '@/vue-app/utils/currency';
import { v4 } from 'uuid';

// Application
import {
  SearchAllianzAccountReportQuery,
} from '@/modules/my-investment/allianz/allianz-account-report/application/queries';
import InvestmentSuggestionCommand
  from '@/modules/investment-suggestion/application/commands/investment-suggestion-command';
import CustomerOverviewService from '@/modules/home-dashboard/customer-overview/application/services/customer-overview-service';

// Domain
import {
  InvestmentSuggestionDto,
} from '@/modules/investment-suggestion/domain/dtos/investment-suggestion-dto';
import Inject from '@/modules/shared/domain/di/inject';
import { Router } from '@/modules/shared/domain/router';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';
import { DateFormatter } from '@/modules/shared/domain/date-formatters';

export default class SowosWealthCardViewModel {
  @Inject(TYPES.SEARCH_ALLIANZ_ACCOUNT_REPORT_QUERY)
  private readonly search_allianz_account_report_query!: SearchAllianzAccountReportQuery;

  @Inject(TYPES.CREATE_INVESTMENT_SUGGESTION_COMMAND)
  private readonly create_investment_suggestion_command!: InvestmentSuggestionCommand;

  @Inject(TYPES.CUSTOMER_OVERVIEW_SERVICE)
  private readonly customer_overview_service!: CustomerOverviewService;

  @Inject(TYPES.ROUTER)
  private readonly router!: Router;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  @Inject(TYPES.DATE_FORMATTER)
  readonly date_formatter!: DateFormatter;

  private readonly customer_id = sessionStorage.getItem('user_id');

  readonly i18n_namespace = 'components.my-investment-dashboard.sowos-wealth-card';

  private readonly error_allianz_account_not_exists = `Error customer_id:${this.customer_id}, not exists`;

  private readonly error_allianz_account_report_not_exits = 'allianz_account_report_by_customer_id_list:[], not exists';

  readonly asset = 'icon_ahorro.svg';

  is_loading = false;

  balance = '0.00 MXN';

  updated_date = '';

  has_allianz_account = false;

  bg_image = {
    backgroundImage: '@/assets/images/investment-dashboard/background_blue.svg',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPositionX: 'right',
    backgroundPositionY: 'bottom',
  }

  is_policy_on_issue_process = false;

  is_status_account_manual = false;

  is_first_interaction_with_dashboard = false;

  first_contribution_was_applied = false;

  allianz_policy_number = '';

  show_status_allianz_modal = false;

  display_contract_savings = false;

  show_contract_savings_modal = false;

  show_info = false;

  show_recommended = false;

  wealth_is_available_to_contract = false;

  product_unavailable_to_hire = '';

  private investment_suggestion_dto: InvestmentSuggestionDto = {
    id: v4(),
    investment_suggestion_analytic_id: '',
    action: 'allianz',
  }

  customer_overview = this.customer_overview_service.get_customer_overview();

  loadAllianzBalance = async () => {
    try {
      const allianz_account_report_list = await this.search_allianz_account_report_query.execute(
        this.customer_id,
      );
      if (allianz_account_report_list.length) {
        const allianz_balance = allianz_account_report_list.reduce(
          (total, current) => total + current.final_balance_mxn!, 0,
        );
        this.balance = `${currencyFormat(allianz_balance)} MXN`;
        const { created_at } = allianz_account_report_list[0];
        this.updated_date = created_at ? this.date_formatter.formatDate(created_at, 'DD MMM YYYY') : this.translate('not_loaded_date');
      }
    } catch (error) {
      if (error.message !== this.error_allianz_account_report_not_exits) {
        this.message_notifier.showErrorNotification(
          i18n.tc(`${this.i18n_namespace}.error_message`),
        );
      }
    }
  }

  get step_number_policy_status() {
    // eslint-disable-next-line no-nested-ternary
    if (this.allianz_policy_number && !this.first_contribution_was_applied) {
      return 2;
    }
    if (this.has_allianz_account && this.is_first_interaction_with_dashboard) {
      return 3;
    }
    return 1;
  }

  get policy_on_review_or_pending_first_contribution() {
    return this.is_policy_on_issue_process || this.policy_with_pending_first_contribution
      || this.is_status_account_manual;
  }

  get policy_with_pending_first_contribution() {
    return this.allianz_policy_number && !this.first_contribution_was_applied;
  }

  validateIfPolicyIsOnProcess = () => {
    const { contracting_status } = this.customer_overview.item.wealth_investment_product_overview;
    this.is_status_account_manual = contracting_status === 'MANUAL_REVIEW';
    this.is_policy_on_issue_process = this.is_status_account_manual;
  }

  validateFirstInteraction = () => {
    this.first_contribution_was_applied = this.customer_overview.item
      .wealth_investment_product_overview.wealth_contract_overview.was_issued;
    this.allianz_policy_number = this.customer_overview.item
      .wealth_investment_product_overview.wealth_contract_overview.policy_number;
    this.is_first_interaction_with_dashboard = !this.customer_overview.item
      .wealth_investment_product_overview.first_interaction_with_dashboard;
    this.is_policy_on_issue_process = !this.customer_overview.item
      .wealth_investment_product_overview.wealth_contract_overview.was_issued;
  }

  loadAllianzData = async () => {
    try {
      if (this.first_contribution_was_applied) {
        this.has_allianz_account = true;
        await this.loadAllianzBalance();
      }
    } catch (error) {
      if (error.message !== this.error_allianz_account_not_exists) {
        this.message_notifier.showErrorNotification('Ha ocurrido un error, inténtelo nuevamente');
      }
    }
  }

  setCardState = async () => {
    if (this.customer_overview.item.wealth_investment_product_overview.contracted) {
      if (this.customer_overview.item.wealth_investment_product_overview.wealth_contract_overview
        .was_issued) {
        this.validateFirstInteraction();
        await this.loadAllianzData();
      } else {
        this.validateFirstInteraction();
      }
    } else if (this.customer_overview.item.wealth_investment_product_overview.contracting_status) {
      this.validateIfPolicyIsOnProcess();
    }
  }

  goToWealthDashboard = () => {
    if (this.is_first_interaction_with_dashboard) {
      this.show_status_allianz_modal = true;
    }
    if (!this.is_first_interaction_with_dashboard) {
      this.router.navigate('sowos-wealth');
    }
  }

  showInfo = ($event: Event) => {
    $event.stopPropagation();
    this.show_info = true;
  }

  start = async () => {
    if (!this.customer_overview.item.wealth_investment_product_overview.available_to_contract) {
      this.product_unavailable_to_hire = 'SOWOS Wealth';
    } else {
      await this.createInvestmentSuggestionAction();
      this.display_contract_savings = true;
      this.show_contract_savings_modal = true;
    }
  }

  showStatus = () => {
    this.show_status_allianz_modal = true;
  }

  translate = (value: string, count?: number, values?: Record<string, string | number>) => (values ? i18n.tc(`${this.i18n_namespace}.${value}`, count, values) as string : i18n.t(`${this.i18n_namespace}.${value}`) as string);

  setBackgroundImage = () => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires,global-require
    const bg_desktop = require('@/assets/images/investment-dashboard/background_blue.svg');
    // eslint-disable-next-line @typescript-eslint/no-var-requires,global-require
    const bg_mobile = require('@/assets/images/investment-dashboard/background_blue.png');
    const { contracted } = this.customer_overview.item.wealth_investment_product_overview;
    const { was_issued } = this.customer_overview.item.wealth_investment_product_overview
      .wealth_contract_overview;
    if (contracted && was_issued) {
      const is_mobile = window.innerWidth < 480;
      this.bg_image.backgroundImage = `url(${is_mobile ? bg_mobile : bg_desktop})`;
      this.bg_image.backgroundSize = 'cover';
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-var-requires,global-require
    const join_wealth = require('@/assets/images/investment-dashboard/join_wealth.svg');
    this.bg_image.backgroundImage = `url(${join_wealth})`;
    this.bg_image.backgroundSize = 'contain';
  }

  setRecommendedProduct = () => {
    if (this.customer_overview.item.investor_profile_overview.recommended_investment_product
      && !this.customer_overview.item.wealth_investment_product_overview.contracting_status) {
      this.show_recommended = (this.customer_overview.item.investor_profile_overview
        .recommended_investment_product.name === 'sowos_wealth');
    }
  }

  createInvestmentSuggestionAction = async () => {
    try {
      if (this.customer_overview.item.investment_suggestion_overview.must_be_shown) {
        this.investment_suggestion_dto.investment_suggestion_analytic_id = this.customer_overview
          .item.investment_suggestion_overview.investment_suggestion_analytic_id;
        await this.create_investment_suggestion_command.execute(this.investment_suggestion_dto);
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.create_investment_suggestion_action'));
    }
  }

  loadCustomerOverview = async () => {
    if (!this.customer_overview.item.wealth_investment_product_overview.contracted) {
      this.setRecommendedProduct();
    }
    this.setBackgroundImage();
    await this.setCardState();
  }

  async initialize() {
    this.is_loading = true;
    await this.loadCustomerOverview();
    this.is_loading = false;
  }
}
